import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Space,
  Segmented,
  Upload,
  Alert,
  Button,
  List,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  BookOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  CloseOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  FileOutlined,
  InboxOutlined,
  LoadingOutlined,
  RobotOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { mekakApi } from '../services';
import type { KnowledgeCreateRequest, UploadQueueItem, UploadQueueStatus } from '../models';
import { UPLOAD_QUEUE_MAX } from '../models';
import { useAuth } from '../../../contexts/AuthContext';
import { useFileUploadQueue } from '../handlers';

interface CreateKnowledgeModalProps {
  open: boolean;
  folderID?: string | null;
  onClose: () => void;
  onCreated: () => void;
}

const STATUS_META: Record<UploadQueueStatus, { color: string; label: string; icon: React.ReactNode }> = {
  waiting:   { color: 'default', label: '排隊中',   icon: <ClockCircleOutlined /> },
  uploading: { color: 'processing', label: '上傳中', icon: <LoadingOutlined spin /> },
  submitted: { color: 'success', label: '已送出',   icon: <CheckCircleFilled style={{ color: '#52c41a' }} /> },
  failed:    { color: 'error',   label: '失敗',     icon: <CloseCircleFilled style={{ color: '#ff4d4f' }} /> },
};

const CreateKnowledgeModal: React.FC<CreateKnowledgeModalProps> = ({
  open,
  folderID,
  onClose,
  onCreated,
}) => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [inputMode, setInputMode] = useState<'text' | 'file'>('text');
  const queue = useFileUploadQueue();
  // Track each previous fileList object so beforeUpload only enqueues once per drop batch.
  const lastBatchRef = useRef<unknown>(null);
  // Refresh parent once per newly-submitted file so the file list polling can pick it up.
  const lastSubmittedSeenRef = useRef(0);

  useEffect(() => {
    if (queue.submittedCount > lastSubmittedSeenRef.current) {
      lastSubmittedSeenRef.current = queue.submittedCount;
      onCreated();
    }
  }, [queue.submittedCount, onCreated]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);

      const payload: KnowledgeCreateRequest = {
        content: values.content.trim(),
        author: user?.userName || 'user',
        tags: values.tags ?? [],
      };

      await mekakApi.createKnowledge(
        folderID ? { ...payload, folder_id: folderID } as KnowledgeCreateRequest & { folder_id: string } : payload,
      );
      message.success('知識條目建立成功，AI 正在進行預處理與向量化');
      form.resetFields();
      onCreated();
      onClose();
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'errorFields' in error) return; // form validation
      const errorMessage =
        error instanceof Error ? error.message : '建立失敗，請稍後再試';
      message.error(`建立失敗：${errorMessage}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  const tryClose = () => {
    if (queue.hasInflight) {
      message.warning('檔案上傳中，請稍候');
      return;
    }
    form.resetFields();
    setInputMode('text');
    queue.reset();
    lastBatchRef.current = null;
    lastSubmittedSeenRef.current = 0;
    onClose();
  };

  const renderQueueItem = (item: UploadQueueItem) => {
    const meta = STATUS_META[item.status];
    const removable = item.status !== 'uploading';
    return (
      <List.Item
        key={item.uid}
        actions={[
          removable ? (
            <Tooltip title="從清單移除" key="remove">
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => queue.removeItem(item.uid)}
              />
            </Tooltip>
          ) : null,
        ].filter(Boolean) as React.ReactNode[]}
      >
        <List.Item.Meta
          avatar={meta.icon}
          title={
            <Space size={8}>
              <span style={{ wordBreak: 'break-all' }}>{item.file.name}</span>
              <Tag color={meta.color}>{meta.label}</Tag>
            </Space>
          }
          description={item.message || `${(item.file.size / 1024).toFixed(1)} KB`}
        />
      </List.Item>
    );
  };

  const fileFooter = (() => {
    if (queue.items.length === 0) {
      return [
        <Button key="cancel" onClick={tryClose}>
          取消
        </Button>,
      ];
    }
    if (queue.hasInflight) {
      return [
        <Button key="cancel" disabled>
          上傳中…
        </Button>,
      ];
    }
    // allSettled
    return [
      <Button key="done" type="primary" onClick={tryClose}>
        完成
      </Button>,
    ];
  })();

  return (
    <Modal
      title="新增知識條目"
      open={open}
      onCancel={tryClose}
      width={720}
      maskClosable={!queue.hasInflight}
      closable={!queue.hasInflight}
      footer={
        inputMode === 'text'
          ? [
              <Button key="cancel" onClick={tryClose}>
                取消
              </Button>,
              <Button
                key="submit"
                type="primary"
                icon={<SaveOutlined />}
                loading={submitLoading}
                onClick={handleSubmit}
              >
                建立條目
              </Button>,
            ]
          : fileFooter
      }
      destroyOnClose
    >
      {/* Mode selector */}
      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
        <Segmented
          options={[
            { label: '手動輸入', value: 'text', icon: <BookOutlined /> },
            { label: '檔案上傳', value: 'file', icon: <FileOutlined /> },
          ]}
          value={inputMode}
          onChange={(val) => setInputMode(val as 'text' | 'file')}
          block
          disabled={queue.hasInflight}
        />
      </Space>

      {/* Text input mode */}
      {inputMode === 'text' && (
        <>
          <Alert
            icon={<RobotOutlined />}
            message="AI 自動預處理"
            description="提交後系統將自動使用 Gemini AI 進行智能切割、摘要與向量化。標題由內容首行自動產生。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form form={form} layout="vertical" requiredMark="optional">
            <Form.Item
              label="內容"
              name="content"
              rules={[
                { required: true, message: '請輸入內容' },
                { whitespace: true, message: '內容不能為空白' },
              ]}
            >
              <Input.TextArea
                placeholder="請詳細描述此知識條目的內容。&#10;系統會自動以內容的第一行作為標題。"
                rows={8}
                showCount
                allowClear
                style={{ resize: 'vertical' }}
              />
            </Form.Item>
            <Form.Item
              label="標籤"
              name="tags"
              extra="輸入標籤名稱後按 Enter 建立"
            >
              <Select
                mode="tags"
                placeholder="輸入並按 Enter 新增標籤"
                tokenSeparators={[',', '，']}
                allowClear
                style={{ width: '100%' }}
                maxTagCount="responsive"
              />
            </Form.Item>
          </Form>
        </>
      )}

      {/* File upload mode */}
      {inputMode === 'file' && (
        <>
          <Alert
            icon={<RobotOutlined />}
            message="檔案上傳說明"
            description={`一次最多 ${UPLOAD_QUEUE_MAX} 個檔案、單檔上限 200 MB，依序送出後由後端背景處理。支援 PPTX、PDF 與圖片（PNG/JPG/WEBP/GIF）。送出後可關閉視窗，列表會自動更新。`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Upload.Dragger
            name="file"
            multiple
            accept=".pptx,.pdf,.png,.jpg,.jpeg,.webp,.gif"
            showUploadList={false}
            beforeUpload={(_file, fileList) => {
              // beforeUpload fires per file but `fileList` is identical across the batch.
              // Enqueue once per batch by comparing the list reference.
              if (lastBatchRef.current !== fileList) {
                lastBatchRef.current = fileList;
                queue.enqueue(fileList as unknown as File[], folderID);
              }
              return Upload.LIST_IGNORE;
            }}
            disabled={queue.items.length >= UPLOAD_QUEUE_MAX}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {queue.items.length >= UPLOAD_QUEUE_MAX
                ? `已達 ${UPLOAD_QUEUE_MAX} 檔上限，請先移除部分項目`
                : '點擊或拖曳檔案到此處（可多選）'}
            </p>
            <p className="ant-upload-hint">
              支援 .pptx、.pdf 和圖片（.png/.jpg/.webp/.gif）
            </p>
          </Upload.Dragger>

          {queue.items.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Space style={{ marginBottom: 8 }} size="small" wrap>
                <Tag>共 {queue.items.length} / {UPLOAD_QUEUE_MAX}</Tag>
                {queue.submittedCount > 0 && (
                  <Tag color="success">已送出 {queue.submittedCount}</Tag>
                )}
                {queue.failedCount > 0 && (
                  <Tag color="error">失敗 {queue.failedCount}</Tag>
                )}
                {queue.allSettled && (
                  <Tooltip title="清空清單">
                    <Button
                      size="small"
                      type="text"
                      icon={<CloseOutlined />}
                      onClick={() => {
                        queue.reset();
                        lastBatchRef.current = null;
                        lastSubmittedSeenRef.current = 0;
                      }}
                    >
                      清空
                    </Button>
                  </Tooltip>
                )}
              </Space>
              <List
                size="small"
                bordered
                dataSource={queue.items}
                renderItem={renderQueueItem}
                style={{ maxHeight: 320, overflowY: 'auto' }}
              />
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default CreateKnowledgeModal;
